'use client';

import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useState } from 'react';
import { GENERIC_IMAGE_PLACEHOLDER_PATH } from '../../../../shared/constants/images.constants';

type ImageDisplayProps = {
	src?: string | null;
	alt?: string;
	className?: string;
	width?: number | string;
	height?: number | string;
	rounded?: boolean;
	fallbackSrc?: string;
	useFallback?: boolean;
	isLoading?: boolean;
};

export function ImageDisplay({
	src,
	alt = 'Image',
	className,
	width = 48,
	height = 48,
	rounded = true,
	fallbackSrc = GENERIC_IMAGE_PLACEHOLDER_PATH,
	useFallback = true,
	isLoading = false,
}: ImageDisplayProps) {
	const [hasLoaded, setHasLoaded] = useState(false);
	const [hasError, setHasError] = useState(false);

	const sizeStyle = {
		width: typeof width === 'number' ? `${width}px` : width,
		height: typeof height === 'number' ? `${height}px` : height,
	};

	const imageClass = cn(
		'object-cover',
		sizeStyle,
		rounded ? 'rounded-full' : 'rounded-md',
		'transition-opacity duration-300',
		hasLoaded ? 'opacity-100' : 'opacity-0',
		className
	);

	const skeletonClass = cn(
		'bg-muted',
		rounded ? 'rounded-full' : 'rounded-md'
	);

	// Show skeleton if loading or waiting for image to load
	const shouldShowSkeleton = isLoading || (!!src && !hasLoaded && !hasError);

	const showImageTag =
		!isLoading &&
		((!!src && !hasError) || (useFallback && (!src || hasError)));

	const imageSrc = !src && useFallback ? fallbackSrc : src;

	return (
		<div className="relative overflow-hidden" style={sizeStyle}>
			{shouldShowSkeleton && (
				<Skeleton className={cn('absolute inset-0', skeletonClass)} />
			)}

			{showImageTag && imageSrc && (
				<img
					src={imageSrc}
					alt={alt}
					style={sizeStyle}
					onLoad={() => setHasLoaded(true)}
					onError={() => setHasError(true)}
					className={imageClass}
					loading="lazy"
				/>
			)}
		</div>
	);
}

export default ImageDisplay;
